import * as React from 'react'
import clsx from 'clsx'
import * as plume from '@ulule/owl-kit-components/next'

import { type Sticker } from '../content'
import styles from './StickerCard.module.css'

export type StickerCardProps = {
  sticker: Sticker
}

/**
 * One campaign sticker: a PNG preview with PNG + SVG download buttons. When the sticker has variants
 * (e.g. "Objectif" at 100–500 %), a dropdown swaps the preview and the downloads in place, instead of
 * adding a card per value. Interactive (useState) so it hydrates; initial index 0 is deterministic, so
 * the SSR markup matches hydration.
 */
export function StickerCard({ sticker }: StickerCardProps): React.ReactElement {
  const { variants } = sticker
  const [index, setIndex] = React.useState(0)

  const active = variants !== undefined ? variants[index] : sticker
  const suffix = variants !== undefined ? ` ${variants[index].label}` : ''
  const pngDownload = variants !== undefined ? `ulule-${variants[index].slug}.png` : true
  const svgDownload = variants !== undefined ? `ulule-${variants[index].slug}.svg` : true

  return (
    <figure className={styles.sticker}>
      <div className={styles.sticker__stage}>
        <img src={active.png} alt={`${sticker.name}${suffix}`} loading="lazy" />
      </div>
      <figcaption className={styles.sticker__foot}>
        <p className={styles.sticker__name}>{sticker.name}</p>

        {variants !== undefined ? (
          <label className={styles.variant}>
            <span className={styles.variant__label}>Valeur</span>
            <select
              className={styles.variant__select}
              value={index}
              onChange={(event) => setIndex(Number(event.target.value))}
            >
              {variants.map((variant, variantIndex) => (
                <option key={variant.slug} value={variantIndex}>
                  {variant.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className={styles.sticker__actions}>
          <a className={clsx(styles.chip, styles.chipPrimary)} href={active.png} download={pngDownload}>
            <plume.glyphs.stroke.Download size={14} />
            PNG
          </a>
          <a className={styles.chip} href={active.svg} download={svgDownload}>
            <plume.glyphs.stroke.CodeBlock size={14} />
            SVG
          </a>
        </div>
      </figcaption>
    </figure>
  )
}
